import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App, Form } from 'antd'
import type { CustomerBatchAddTagsInput } from '@super-mega/server/contracts/customer'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { CustomerTagSelect } from './customer-tag-select.tsx'

type Values = Omit<CustomerBatchAddTagsInput, 'ids'>

export function CustomerBatchTagsModal({
  ids,
  onClose,
  onSaved
}: {
  ids: number[]
  onClose: () => void
  onSaved: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const [open, setOpen] = useState(true)
  const mutation = useMutation(trpc.customer.batchAddTags.mutationOptions())
  return (
    <ModalForm<Values>
      open={open}
      title="批量添加客户标签"
      layout="vertical"
      width="32rem"
      initialValues={{ tagIds: [] }}
      modalProps={{
        destroyOnHidden: true,
        mask: { closable: false },
        afterClose: () => {
          if (mutation.isSuccess) onSaved()
          else onClose()
        },
        keyboard: !mutation.isPending,
        closable: !mutation.isPending
      }}
      onOpenChange={(nextOpen) => {
        if (!mutation.isPending) setOpen(nextOpen)
      }}
      submitter={{
        searchConfig: { submitText: '添加标签', resetText: '取消' },
        resetButtonProps: { disabled: mutation.isPending },
        submitButtonProps: { loading: mutation.isPending }
      }}
      onFinish={async (values) => {
        if (mutation.isPending) {
          return false
        }
        try {
          const result = await mutation.mutateAsync({ ids, tagIds: values.tagIds })
          await queryClient.invalidateQueries(trpc.customer.list.queryFilter())
          void message.success(`已为 ${result.count} 个客户添加标签`)
          setOpen(false)
          return true
        } catch (error) {
          void message.error(getErrorMessage(error, '批量添加客户标签失败'))
          return false
        }
      }}
    >
      <p className="border-border bg-surface-muted mb-5 rounded-lg border p-3 text-sm">
        将为已选 <strong>{ids.length}</strong> 个客户添加标签。
      </p>
      <p className="text-content-muted mb-4 text-sm">保留已有标签，重复标签自动跳过。每个客户最多保留 50 个标签。</p>
      <Form.Item
        label="客户标签"
        name="tagIds"
        rules={[{ required: true, type: 'array', min: 1, max: 50, message: '请选择 1～50 个客户标签' }]}
      >
        <CustomerTagSelect disabled={mutation.isPending} />
      </Form.Item>
    </ModalForm>
  )
}
